import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Pipe, InjectionToken, signal, Injectable, Inject, Optional, input, output, inject, Component, ChangeDetectionStrategy } from '@angular/core';
import * as i2 from 'angular-i18next';
import { I18NEXT_SERVICE, I18NextModule } from 'angular-i18next';
import Knock from '@knocklabs/client';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as i3 from 'ngx-timeago';
import { TimeagoModule } from 'ngx-timeago';

class GetInitialsPipe {
    transform(value, ...args) {
        const regexChar = /\D\w+/;
        return value
            .trim() // remove trailing spaces
            .split(' ') // splits on spaces
            .filter((word) => word.length > 0) // strip out double spaces
            .filter((word) => regexChar.test(word)) // strip out special characters
            .map((word) => word.substring(0, 1).toUpperCase())
            .join(''); // take first letter from each word
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: GetInitialsPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.8", ngImport: i0, type: GetInitialsPipe, isStandalone: true, name: "getInitials" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: GetInitialsPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'getInitials',
                    standalone: true,
                }]
        }] });

const isToday = (date) => {
    const today = new Date();
    return (date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear());
};
const isYesterday = (date) => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return (date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear());
};

const stripHtmlTags = (input) => {
    if (input) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(input, 'text/html');
        return doc.body.textContent || '';
    }
    return '';
};

var loadMore$1 = "Mehr laden";
var markAllAsRead$1 = "Alle als gelesen markieren";
var noNotifications$1 = "Derzeit nichts zu berichten!";
var notifications$1 = "Benachrichtigungen";
var older$1 = "Älter";
var today$1 = "Heute";
var yesterday$1 = "Gestern";
var translationDE = {
	loadMore: loadMore$1,
	markAllAsRead: markAllAsRead$1,
	noNotifications: noNotifications$1,
	notifications: notifications$1,
	older: older$1,
	today: today$1,
	yesterday: yesterday$1
};

var loadMore = "Load more";
var markAllAsRead = "Mark all as read";
var noNotifications = "Currently, nothing to report!";
var notifications = "Notifications";
var older = "Older";
var today = "Today";
var yesterday = "Yesterday";
var translationEN = {
	loadMore: loadMore,
	markAllAsRead: markAllAsRead,
	noNotifications: noNotifications,
	notifications: notifications,
	older: older,
	today: today,
	yesterday: yesterday
};

const UAG_NOTIFICATIONS_OPTIONS = new InjectionToken('UAG_NOTIFICATIONS_OPTIONS');

const translations = {
    de: translationDE,
    en: translationEN,
};
const translationNamespace = 'angularCore.notifications';
class NotificationsService {
    i18NextService;
    notificationsOptions;
    isLoading = signal(false);
    notifications = signal([]);
    notificationsCount = signal({
        total: 0,
        unread: 0,
        unseen: 0,
    });
    loadedNotifications = signal(0);
    pageSize = 10;
    knockClient;
    knockFeed;
    userImageCache = new Map();
    constructor(i18NextService, notificationsOptions) {
        this.i18NextService = i18NextService;
        this.notificationsOptions = notificationsOptions;
        // load locales
        for (const language of Object.getOwnPropertyNames(translations)) {
            const translationsForLanguage = translations[language];
            this.i18NextService.addResourceBundle(language, translationNamespace, translationsForLanguage, false, false);
        }
        // update state to update group texts
        this.i18NextService.events.languageChanged
            .pipe(takeUntilDestroyed())
            .subscribe(async (language) => {
            if (language) {
                await this.updateState();
            }
        });
    }
    ngOnDestroy() {
        this.disconnect();
    }
    async connectToFeed(apiKey, userId, userToken, feedId) {
        this.isLoading.set(true);
        this.knockClient = new Knock(apiKey);
        this.knockClient.authenticate(userId, userToken);
        this.knockFeed = this.knockClient.feeds.initialize(feedId, {
            page_size: this.pageSize,
        });
        this.knockFeed.on('items.received.realtime', this.handleUpdate);
        this.knockFeed.on('items.received.page', this.handleUpdate);
        this.knockFeed.listenForUpdates();
        await this.knockFeed.fetch();
        await this.updateState();
        this.isLoading.set(false);
    }
    disconnect() {
        if (this.knockFeed) {
            this.knockFeed.off('items.received.realtime', this.handleUpdate);
            this.knockFeed.off('items.received.page', this.handleUpdate);
            this.knockFeed.teardown();
        }
    }
    async markAllAsSeen() {
        this.isLoading.set(true);
        setTimeout(async () => {
            if (this.knockFeed && this.notificationsCount().unseen) {
                const state = await this.knockFeed.getState();
                await this.knockFeed.markAsSeen(state.items);
                await this.updateState();
            }
            this.isLoading.set(false);
        }, 0);
    }
    async markAsRead(notification) {
        this.isLoading.set(true);
        setTimeout(async () => {
            if (this.knockFeed) {
                const state = await this.knockFeed.getState();
                await this.knockFeed.markAsRead(state.items.filter((i) => i.id === notification.id));
                await this.updateState();
            }
            this.isLoading.set(false);
        }, 0);
    }
    async remove(notification) {
        this.isLoading.set(true);
        setTimeout(async () => {
            if (this.knockFeed) {
                const state = await this.knockFeed.getState();
                await this.knockFeed.markAsArchived(state.items.filter((i) => i.id === notification.id));
                await this.updateState();
            }
            this.isLoading.set(false);
        }, 0);
    }
    async markAllAsRead() {
        this.isLoading.set(true);
        setTimeout(async () => {
            if (this.knockFeed) {
                await this.knockFeed.markAllAsRead();
                await this.updateState();
            }
            this.isLoading.set(false);
        }, 0);
    }
    async loadMore() {
        this.isLoading.set(true);
        setTimeout(async () => {
            if (this.knockFeed) {
                await this.knockFeed.fetchNextPage();
            }
            this.isLoading.set(false);
        }, 0);
    }
    handleUpdate = (response) => {
        this.updateState();
    };
    async updateState() {
        if (this.knockFeed) {
            const state = this.knockFeed.getState();
            const items = state.items
                .filter((item) => !!item.data)
                .map((item) => {
                const feedActions = this.parseFeedActions(item.data);
                const action = feedActions && feedActions.length > 0 ? feedActions[0] : null;
                return {
                    id: item.id,
                    title: stripHtmlTags(item.data['houston_subject']),
                    message: stripHtmlTags(item.data['houston_message']),
                    trackingId: item.data['houston_tracking-id'],
                    time: new Date(item.inserted_at),
                    actor: {
                        name: item.actors[0]?.name,
                        type: item.actors[0]?.__typename === 'User'
                            ? 'user'
                            : 'category',
                        categoryImageUrl: item.actors[0]?.properties?.picture_uri,
                    },
                    read: !!item.read_at,
                    internalAction: action?.internal && action.internal_system
                        ? {
                            action: action.internal,
                            system: action.internal_system,
                        }
                        : undefined,
                    externalAction: action?.external,
                };
            });
            // group notifications bei time
            const groups = [
                {
                    title: this.i18NextService.t('angularCore.notifications:today'),
                    notifications: items.filter((i) => isToday(i.time)),
                },
                {
                    title: this.i18NextService.t('angularCore.notifications:yesterday'),
                    notifications: items.filter((i) => isYesterday(i.time)),
                },
                {
                    title: this.i18NextService.t('angularCore.notifications:older'),
                    notifications: items.filter((i) => !isToday(i.time) && !isYesterday(i.time)),
                },
            ].filter((group) => group.notifications.length);
            // load user images
            if (this.notificationsOptions?.getUserImageUrl) {
                const promises = [];
                items
                    .filter((item) => item.actor.type === 'user')
                    .forEach((item) => {
                    if (this.userImageCache.has(item.trackingId)) {
                        item.actor.userImageUrl = this.userImageCache.get(item.trackingId);
                    }
                    else {
                        const promise = this.notificationsOptions.getUserImageUrl(item.trackingId);
                        promises.push(promise);
                        promise.then((response) => {
                            if (response) {
                                item.actor.userImageUrl = response;
                                this.userImageCache.set(item.trackingId, response);
                            }
                        });
                    }
                });
                Promise.all(promises).then(() => {
                    this.notifications.set(groups.slice());
                });
            }
            this.notifications.set(groups);
            this.loadedNotifications.set(state.items.length);
            this.notificationsCount.set({
                total: state.metadata.total_count,
                unread: state.metadata.unread_count,
                unseen: state.metadata.unseen_count,
            });
        }
    }
    parseFeedActions(data) {
        if (!!data && Array.isArray(data['houston_actions'])) {
            return data['houston_actions'];
        }
        return null;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NotificationsService, deps: [{ token: I18NEXT_SERVICE }, { token: UAG_NOTIFICATIONS_OPTIONS, optional: true }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NotificationsService });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NotificationsService, decorators: [{
            type: Injectable
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [I18NEXT_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [UAG_NOTIFICATIONS_OPTIONS]
                }, {
                    type: Optional
                }] }] });

class NotificationsComponent {
    system = input();
    onNotificationClicked = output();
    notificationsService = inject(NotificationsService);
    ngAfterViewInit() {
        void this.notificationsService.markAllAsSeen();
    }
    markAllAsRead(event) {
        event.stopImmediatePropagation();
        void this.notificationsService.markAllAsRead();
    }
    async markAsReadAndPerformAction(event, notification) {
        event.stopImmediatePropagation();
        await this.notificationsService.markAsRead(notification);
        const isInternalAction = notification.internalAction && notification.internalAction.system == this.system();
        if (!isInternalAction && notification.externalAction) {
            window.open(notification.externalAction, '_blank');
        }
        else {
            this.onNotificationClicked.emit(notification);
        }
    }
    remove(event, notification) {
        event.stopImmediatePropagation();
        void this.notificationsService.remove(notification);
    }
    loadMore(event) {
        event.stopImmediatePropagation();
        void this.notificationsService.loadMore();
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NotificationsComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.8", type: NotificationsComponent, isStandalone: true, selector: "uag-notifications", inputs: { system: { classPropertyName: "system", publicName: "system", isSignal: true, isRequired: false, transformFunction: null } }, outputs: { onNotificationClicked: "onNotificationClicked" }, ngImport: i0, template: "<div class=\"notification-panel\">\n  <!-- header -->\n  <div class=\"notification-panel-header\">\n    <span class=\"notification-panel-header-label\">{{\n      \"angularCore.notifications:notifications\" | i18next\n    }}</span>\n    <button\n      class=\"notification-panel-header-button\"\n      *ngIf=\"notificationsService.notifications().length\"\n      [disabled]=\"!notificationsService.notificationsCount().unread\"\n      (click)=\"markAllAsRead($event)\"\n    >\n      {{ \"angularCore.notifications:markAllAsRead\" | i18next }}\n    </button>\n  </div>\n\n  <!-- groups -->\n  <ng-container *ngIf=\"notificationsService.notifications().length; else empty\">\n    <div class=\"notification-panel-items\">\n      @for (group of notificationsService.notifications(); track group.title) {\n      <div class=\"notification-panel-group\">{{ group.title }}</div>\n\n      <!-- items -->\n      @for (notification of group.notifications; track notification.id) {\n      <div class=\"notification-panel-item-wrapper\">\n        <button\n          class=\"notification-panel-item\"\n          (click)=\"markAsReadAndPerformAction($event, notification)\"\n        >\n          <!-- image -->\n          <div\n            className=\"avatar avatar-sm\"\n            *ngIf=\"\n              notification.actor.categoryImageUrl ||\n                notification.actor.userImageUrl;\n              else imagePlaceholder\n            \"\n          >\n            <img\n              alt=\"alt_img\"\n              className=\"rounded-full min-w-8 h-8\"\n              [src]=\"\n                notification.actor.categoryImageUrl ??\n                notification.actor.userImageUrl\n              \"\n            />\n          </div>\n\n          <ng-template #imagePlaceholder>\n            <div class=\"avatar avatar-sm avatar-placeholder\">\n              {{ notification.actor.name ?? \"\" | getInitials }}\n            </div>\n          </ng-template>\n\n          <span\n            class=\"notification-indicator notification-indicator-small\"\n            *ngIf=\"!notification.read\"\n          ></span>\n          <!-- content -->\n          <div class=\"notification-panel-item-content\">\n            <span class=\"notification-panel-item-title\">{{\n              notification.title\n            }}</span>\n            <span class=\"notification-panel-item-description\">\n              {{ notification.message }}\n            </span>\n            <span class=\"notification-panel-item-date\">\n              <div timeago [date]=\"notification.time\"></div>\n            </span>\n          </div>\n        </button>\n        <button\n          (click)=\"remove($event, notification)\"\n          class=\"notification-panel-item-button\"\n        ></button>\n      </div>\n      } }\n      <!-- load more -->\n      <button\n        class=\"notification-panel-button\"\n        *ngIf=\"\n          notificationsService.notificationsCount().total >\n          notificationsService.loadedNotifications()\n        \"\n        (click)=\"loadMore($event)\"\n      >\n        {{ \"angularCore.notifications:loadMore\" | i18next }}\n      </button>\n    </div>\n  </ng-container>\n\n  <!-- empty -->\n  <ng-template #empty>\n    <div class=\"notification-panel-empty\">\n      <span class=\"notification-panel-empty-label\">\n        {{ \"angularCore.notifications:noNotifications\" | i18next }}\n      </span>\n    </div>\n  </ng-template>\n\n  <!-- loading indicator -->\n  <div class=\"spinner-container\" *ngIf=\"notificationsService.isLoading()\">\n    <span\n      class=\"spinner rounded-full h-10 w-10 m-auto border-4 border-border-accent border-r-background-transparent m_5ae2e3c mantine-Loader-root\"\n      data-variant=\"loadingAnimation\"\n    ></span>\n  </div>\n</div>\n", styles: [".spinner-container{position:absolute;width:100%;height:100%;display:flex;justify-content:center;background-color:#0002}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: GetInitialsPipe, name: "getInitials" }, { kind: "ngmodule", type: I18NextModule }, { kind: "pipe", type: i2.I18NextPipe, name: "i18next" }, { kind: "ngmodule", type: TimeagoModule }, { kind: "directive", type: i3.TimeagoDirective, selector: "[timeago]", inputs: ["date", "live"], exportAs: ["timeago"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: NotificationsComponent, decorators: [{
            type: Component,
            args: [{ selector: 'uag-notifications', standalone: true, imports: [CommonModule, GetInitialsPipe, I18NextModule, TimeagoModule], changeDetection: ChangeDetectionStrategy.OnPush, template: "<div class=\"notification-panel\">\n  <!-- header -->\n  <div class=\"notification-panel-header\">\n    <span class=\"notification-panel-header-label\">{{\n      \"angularCore.notifications:notifications\" | i18next\n    }}</span>\n    <button\n      class=\"notification-panel-header-button\"\n      *ngIf=\"notificationsService.notifications().length\"\n      [disabled]=\"!notificationsService.notificationsCount().unread\"\n      (click)=\"markAllAsRead($event)\"\n    >\n      {{ \"angularCore.notifications:markAllAsRead\" | i18next }}\n    </button>\n  </div>\n\n  <!-- groups -->\n  <ng-container *ngIf=\"notificationsService.notifications().length; else empty\">\n    <div class=\"notification-panel-items\">\n      @for (group of notificationsService.notifications(); track group.title) {\n      <div class=\"notification-panel-group\">{{ group.title }}</div>\n\n      <!-- items -->\n      @for (notification of group.notifications; track notification.id) {\n      <div class=\"notification-panel-item-wrapper\">\n        <button\n          class=\"notification-panel-item\"\n          (click)=\"markAsReadAndPerformAction($event, notification)\"\n        >\n          <!-- image -->\n          <div\n            className=\"avatar avatar-sm\"\n            *ngIf=\"\n              notification.actor.categoryImageUrl ||\n                notification.actor.userImageUrl;\n              else imagePlaceholder\n            \"\n          >\n            <img\n              alt=\"alt_img\"\n              className=\"rounded-full min-w-8 h-8\"\n              [src]=\"\n                notification.actor.categoryImageUrl ??\n                notification.actor.userImageUrl\n              \"\n            />\n          </div>\n\n          <ng-template #imagePlaceholder>\n            <div class=\"avatar avatar-sm avatar-placeholder\">\n              {{ notification.actor.name ?? \"\" | getInitials }}\n            </div>\n          </ng-template>\n\n          <span\n            class=\"notification-indicator notification-indicator-small\"\n            *ngIf=\"!notification.read\"\n          ></span>\n          <!-- content -->\n          <div class=\"notification-panel-item-content\">\n            <span class=\"notification-panel-item-title\">{{\n              notification.title\n            }}</span>\n            <span class=\"notification-panel-item-description\">\n              {{ notification.message }}\n            </span>\n            <span class=\"notification-panel-item-date\">\n              <div timeago [date]=\"notification.time\"></div>\n            </span>\n          </div>\n        </button>\n        <button\n          (click)=\"remove($event, notification)\"\n          class=\"notification-panel-item-button\"\n        ></button>\n      </div>\n      } }\n      <!-- load more -->\n      <button\n        class=\"notification-panel-button\"\n        *ngIf=\"\n          notificationsService.notificationsCount().total >\n          notificationsService.loadedNotifications()\n        \"\n        (click)=\"loadMore($event)\"\n      >\n        {{ \"angularCore.notifications:loadMore\" | i18next }}\n      </button>\n    </div>\n  </ng-container>\n\n  <!-- empty -->\n  <ng-template #empty>\n    <div class=\"notification-panel-empty\">\n      <span class=\"notification-panel-empty-label\">\n        {{ \"angularCore.notifications:noNotifications\" | i18next }}\n      </span>\n    </div>\n  </ng-template>\n\n  <!-- loading indicator -->\n  <div class=\"spinner-container\" *ngIf=\"notificationsService.isLoading()\">\n    <span\n      class=\"spinner rounded-full h-10 w-10 m-auto border-4 border-border-accent border-r-background-transparent m_5ae2e3c mantine-Loader-root\"\n      data-variant=\"loadingAnimation\"\n    ></span>\n  </div>\n</div>\n", styles: [".spinner-container{position:absolute;width:100%;height:100%;display:flex;justify-content:center;background-color:#0002}\n"] }]
        }] });

/*
 * Public API Surface of angular-core
 */

/**
 * Generated bundle index. Do not edit.
 */

export { NotificationsComponent, NotificationsService, UAG_NOTIFICATIONS_OPTIONS };
